import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../TailwindComponents/Button";
import { PiXLight, PiUsersLight, PiSparkleLight, PiCrownLight } from "react-icons/pi";
import { LicensePlan } from "../../../store/actions/PlanActions";

export interface FullPlanSuggestion {
  fullPlan: LicensePlan;
  fullPlanPrice: number;
  currentTotalWithAddon: number;
}

interface AddAddonModalProps {
  open: boolean;
  onClose: () => void;
  addon: LicensePlan | null;
  billingPeriod: "monthly" | "annual";
  teamSize: number;
  onConfirm: (addon: LicensePlan, seatCount: number, applyTeamDiscount: boolean) => Promise<void>;
  fullPlanSuggestion?: FullPlanSuggestion | null;
  onUpgradeToFull?: (plan: LicensePlan) => void;
}

const AddAddonModal: React.FC<AddAddonModalProps> = ({
  open,
  onClose,
  addon,
  billingPeriod,
  teamSize,
  onConfirm,
  fullPlanSuggestion,
  onUpgradeToFull,
}) => {
  const { t } = useTranslation();
  const [seatInput, setSeatInput] = useState("1");
  const [wholeTeam, setWholeTeam] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSeatInput("1");
      setWholeTeam(false);
    }
  }, [open]);

  if (!addon) return null;

  const price = billingPeriod === "monthly" ? addon.price_monthly : addon.price_annual;
  const teamDiscount = addon.team_discount_percent || 0;
  const teamPrice = teamDiscount > 0 ? price * (1 - teamDiscount / 100) : price;

  const seatCount = wholeTeam ? teamSize : Math.max(1, parseInt(seatInput, 10) || 1);
  const effectivePrice = wholeTeam && teamDiscount > 0 ? teamPrice : price;
  const totalMonthly = effectivePrice * seatCount;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(addon, seatCount, wholeTeam && teamDiscount > 0);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <PiSparkleLight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <Dialog.Title className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                    {t("Add")} {addon.display_name}
                  </Dialog.Title>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <PiXLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </button>
              </div>

              {/* Body */}
              <div className="p-4 space-y-4">
                {/* Team toggle */}
                {addon.is_per_seat && teamDiscount > 0 && teamSize > 1 && (
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={wholeTeam}
                      onChange={(e) => setWholeTeam(e.target.checked)}
                      className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {t("Buy for whole team")} ({teamSize} {t("members")})
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {t("Save")} {teamDiscount}% — ${teamPrice.toFixed(0)}/{t("seat")}/{t("mo")}
                      </p>
                    </div>
                  </label>
                )}

                {/* Seat count input (only when not whole team) */}
                {addon.is_per_seat && !wholeTeam && (
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                      {t("Number of seats")}
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={seatInput}
                      onChange={(e) => setSeatInput(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                )}

                {/* Cost summary */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between text-gray-500 dark:text-gray-400">
                    <span>{t("Price per seat")}</span>
                    <span>
                      ${effectivePrice.toFixed(2)}/{t("mo")}
                      {wholeTeam && teamDiscount > 0 && (
                        <span className="line-through text-gray-400 ml-1">${price.toFixed(0)}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-500 dark:text-gray-400">
                    <span>{t("Seats")}</span>
                    <span>{seatCount}</span>
                  </div>
                  <hr className="border-gray-200 dark:border-gray-700" />
                  <div className="flex justify-between text-gray-900 dark:text-gray-100 font-medium">
                    <span>{t("Total")}</span>
                    <span>
                      ${totalMonthly.toFixed(2)}/{t("mo")}
                      {billingPeriod === "annual" && (
                        <span className="text-gray-400 font-normal ml-1">
                          ({t("billed annually")})
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Full-plan value nudge */}
                {fullPlanSuggestion && fullPlanSuggestion.fullPlanPrice <= fullPlanSuggestion.currentTotalWithAddon && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-3 cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                    onClick={() => {
                      onClose();
                      onUpgradeToFull?.(fullPlanSuggestion.fullPlan);
                    }}
                  >
                    <PiCrownLight className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {t("Consider upgrading to {{plan}} at ${{price}}/mo — includes this and all other add-ons.", {
                        plan: fullPlanSuggestion.fullPlan.display_name,
                        price: fullPlanSuggestion.fullPlanPrice.toFixed(0),
                      })}
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {t("You will be charged immediately for the prorated amount.")}
                </p>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="neutral" size="small" onClick={onClose}>
                  {t("Cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleConfirm}
                  isLoading={loading}
                >
                  {t("Add Add-on")}
                </Button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddAddonModal;
